import * as React from 'react';

import {Button} from 'shells/button/button';
import {Card} from 'shells/card/card';
import {colors} from 'shells/colors/colors';
import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {ListView} from 'shells/list-view/list-view';

import type {
    WfFormFragment_publishedVersion_fields as Field,
    FormFieldUpdateInput,
} from 'nutshell-graphql-types';

import type {FormFieldSchema} from '../../core/nutshell-fields/types';

import {FieldMappingCardWrapper} from './field-mapping-card-wrapper';
import {getFieldExamplesString} from './helpers';

import styles from './connected-forms-field-list.module.css';

type Props = {
    fields: Field[];
    onUpdateField: (arg1: FormFieldUpdateInput) => Promise<any>;
    activeFieldId: string | null | undefined;
    formSchema: FormFieldSchema;
};

export function ConnectedFormsFieldList(props: Props) {
    const [isShowingIgnored, setIsShowingIgnored] = React.useState<boolean>(false);

    const activeFields = props.fields.filter((field) => !field.isIgnored);
    const ignoredFields = props.fields.filter((field) => field.isIgnored);

    const collection = isShowingIgnored ? activeFields.concat(ignoredFields) : activeFields;

    return (
        <div className={styles['field-list']}>
            <ListView
                collection={collection}
                itemSpacing={8}
                renderRow={(field) => {
                    return (
                        <div key={field.id} className={styles['list-item']}>
                            <FieldMappingCardWrapper
                                id={field.id}
                                fieldType={field.type}
                                inputName={field.inputName}
                                exampleValues={getFieldExamplesString(field.exampleValues)}
                                entityName={field.entityName}
                                fieldName={field.fieldName}
                                attributeId={field.attribute && field.attribute.id}
                                isIgnored={field.isIgnored}
                                needsMapping={field.needsMapping}
                                onUpdateField={props.onUpdateField}
                                formSchema={props.formSchema}
                                isActive={props.activeFieldId === field.id}
                                otherFields={
                                    props.fields
                                        ? props.fields.filter(
                                              (otherField) => otherField.id !== field.id
                                          )
                                        : []
                                }
                            />
                        </div>
                    );
                }}
                specialStates={[
                    {
                        shouldRender: () => !props.fields.length,
                        component: (
                            <NothingFoundEmptyState
                                title='No fields found'
                                subtitle='They’ll appear here when there are some to map'
                            />
                        ),
                    },
                ]}
            />
            {ignoredFields.length ? (
                <div className={styles['ignored-card-container']}>
                    <Card
                        hasBorder={true}
                        // @ts-expect-error
                        isFullWidth={true}
                        borderColor={colors.offWhiteDk}
                        onClick={() => {
                            setIsShowingIgnored(!isShowingIgnored);
                        }}
                        fitHeight={true}
                    >
                        <div className={styles['ignored-card']}>
                            {ignoredFields.length} ignored field
                            {ignoredFields.length === 1 ? '' : 's'}{' '}
                            <Button
                                variant='text-primary'
                                onClick={() => {
                                    setIsShowingIgnored(!isShowingIgnored);
                                }}
                            >
                                {isShowingIgnored
                                    ? `Hide ignored field${ignoredFields.length === 1 ? '' : 's'}`
                                    : 'Show all'}
                            </Button>
                        </div>
                    </Card>
                </div>
            ) : undefined}
        </div>
    );
}
